/* eslint-disable no-nested-ternary */
import * as Yup from 'yup';
import { useState } from 'react';
import { useSnackbar } from 'notistack';
import { useQueryClient } from 'react-query';
import Iconify from '@emfood/shared/src/components/iconify';
import { useSearch, useNavigate } from '@tanstack/react-router';
import { useAuthContext, ACHelmetContainer } from '@emfood/shared';
import GoBackHeader from '@emfood/shared/src/components/go-back-header/go-back-header';

import {
  Box,
  Tab,
  Tabs,
  Chip,
  Stack,
  Button,
  Select,
  useTheme,
  MenuItem,
  Accordion,
  Typography,
  InputLabel,
  FormControl,
  AccordionDetails,
  AccordionSummary,
  SelectChangeEvent,
} from '@mui/material';

import {
  useGetCart,
  useGetBuyerSites,
  useEditFavouritesCollection,
  useGetFavouritesCollections,
  useDeleteFavouritesCollection,
} from 'src/api/hooks/api-hooks';

import { useSettingsContext } from 'src/components/settings';
import { LoadingBarHorizontal } from 'src/components/loading';

import EditFavouritesNameDialog from './edit-name-dialog';
import ControlAccessDialog from './control-access-dialog';
import AddFavouritesListDialog from './add-favourites-list-dialog';
import DeleteConfirmationDialog from './delete-confirmation-dialog';
import FavouriteProductsList from './favourite-products/favourite-products-list';

export const FavouritesSearchParamsSchema = Yup.object({
  tab: Yup.mixed<'personal' | 'shared'>()
    .oneOf(['personal', 'shared'] as const)
    .optional()
    .default('personal'),
  buyerSiteId: Yup.string().optional(),
});

export default function Favourites() {
  const settings = useSettingsContext();
  const { user } = useAuthContext();
  const navigate = useNavigate({ from: '/favourites' });
  const searchParams = useSearch({ from: '/favourites/' });
  const currentTab = searchParams.tab;
  const selectedBuyerSiteId = searchParams.buyerSiteId ?? '';

  const isCateringManager =
    user?.roles?.some((role: any) => role.name === 'Catering Manager') || false;

  const [page] = useState(1);
  const [rowsPerPage] = useState(5);
  const [addListOpen, setAddListOpen] = useState(false);
  const [collectionToDelete, setCollectionToDelete] = useState<{ id: string; name: string } | null>(
    null
  );
  const [collectionToEdit, setCollectionToEdit] = useState<any | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [controlAccessOpen, setControlAccessOpen] = useState(false);
  const [selectedCollection, setSelectedCollection] = useState<{
    id: string;
    buyerSites?: Array<{ id: string; name: string }>;
    isSharedWithOrg?: boolean;
  } | null>(null);

  const { data: cart } = useGetCart();
  const { data: buyerSites = [] } = useGetBuyerSites(
    isCateringManager ? user?.buyerOrg?.id || null : null
  );

  const {
    data: favouritesCollections,
    isLoading: isFavouritesCollectionsLoading,
    error: favouritesColllectionsError,
  } = useGetFavouritesCollections(page, rowsPerPage);
  const { mutate: deleteFavouritesCollection } = useDeleteFavouritesCollection();
  const { mutate: updateCollectionName } = useEditFavouritesCollection();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const theme = useTheme();

  // to be uncommented when API is ready for pagination
  //   const handlePageChange = (event: unknown, newPage: number) => {
  //     setPage(newPage);
  //   };

  //   const handleRowsPerPageChange = (event: ChangeEvent<HTMLInputElement>) => {
  //     setRowsPerPage(parseInt(event.target.value, 10));
  //     setPage(0);
  //   };

  const handleDeleteClick = (id: string, name: string) => {
    setCollectionToDelete({ id, name });
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (collectionToDelete) {
      deleteCollection(collectionToDelete.id);
    }
    setDeleteDialogOpen(false);
    setCollectionToDelete(null);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setCollectionToDelete(null);
  };

  const deleteCollection = async (id: string) => {
    deleteFavouritesCollection(
      { pathParams: { collectionId: id } },
      {
        onSuccess: () => {
          enqueueSnackbar('Favourites List is deleted!');
          queryClient.invalidateQueries('favourites');
        },
      }
    );
  };

  const handleEditClick = (
    event: React.MouseEvent<HTMLButtonElement>,
    id: string,
    name: string
  ): void => {
    event.stopPropagation();
    setCollectionToEdit({ id, name });
    setEditDialogOpen(true);
  };

  const handleEditSubmit = (data: { name: string }) => {
    if (collectionToEdit && collectionToEdit.id) {
      updateCollectionName(
        {
          pathParams: { collectionId: collectionToEdit.id },
          body: { name: data.name },
        },
        {
          onSuccess: () => {
            enqueueSnackbar('Favourites list name updated successfully!');
            queryClient.invalidateQueries('favourites');
            setEditDialogOpen(false);
            setCollectionToEdit(null);
          },
        }
      );
    }
  };

  const handleControlAccess = (
    event: React.MouseEvent,
    collectionId: string,
    sites?: Array<{ id: string; name: string }>,
    isOrgShared?: boolean
  ) => {
    event.stopPropagation();
    setSelectedCollection({
      id: collectionId,
      buyerSites: sites,
      isSharedWithOrg: isOrgShared,
    });
    setControlAccessOpen(true);
  };

  const handleCloseControlAccess = () => {
    setControlAccessOpen(false);
    setSelectedCollection(null);
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: 'personal' | 'shared') => {
    navigate({ search: { tab: newValue } });
  };

  const handleBuyerSiteFilterChange = (event: SelectChangeEvent<string>) => {
    const { value } = event.target;
    navigate({
      search: (prev) => ({ ...prev, buyerSiteId: value || undefined }),
      replace: true,
    });
  };

  const getFilteredCollections = () => {
    if (currentTab === 'shared') {
      const orgShared = favouritesCollections?.orgShared || [];
      const siteShared = favouritesCollections?.siteShared || [];
      const combined = [...orgShared, ...siteShared];

      // If a buyer site filter is selected, show:
      // - All orgShared items (they're visible to all sites)
      // - Only siteShared items that match the selected site
      if (selectedBuyerSiteId) {
        return combined.filter((collection: any) => {
          // Include if it's orgShared (has a buyerOrgId)
          const isOrgShared = collection.buyerOrgId !== null;
          // Include if it's shared with the selected site
          const isSharedWithSelectedSite =
            collection.buyerSites?.length > 0 &&
            collection.buyerSites.some((site: any) => site.id === selectedBuyerSiteId);
          return isOrgShared || isSharedWithSelectedSite;
        });
      }

      return combined;
    }

    return favouritesCollections?.[currentTab] || [];
  };

  const filteredCollections = getFilteredCollections();

  const renderAccordions = (collections: any[]) =>
    collections.map((row: any) => {
      const canModify = currentTab === 'personal' || isCateringManager;

      return (
        <Accordion key={row.id} sx={{ bgcolor: theme.palette.background.default }}>
          <AccordionSummary
            expandIcon={<Iconify icon="eva:arrow-ios-downward-fill" />}
            id="panel-header"
            aria-controls="panel-content"
            aria-label={`Expand ${row.name} details`}
          >
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent="flex-start"
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              width="100%"
              spacing={1}
            >
              <Box width={{ xs: '100%', sm: '48%' }}>
                <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" gap={0.5}>
                  {row.buyerOrgId && (
                    <Chip
                      icon={<Iconify icon="eva:people-fill" width={16} />}
                      label="Entire org"
                      size="small"
                      color="info"
                      variant="outlined"
                      sx={{ minWidth: 100 }}
                    />
                  )}
                  {!row.buyerOrgId && row.buyerSites && row.buyerSites.length > 0 && (
                    <Chip
                      icon={<Iconify icon="eva:home-fill" width={16} />}
                      label={`${row.buyerSites.length} site${row.buyerSites.length > 1 ? 's' : ''}`}
                      size="small"
                      color="primary"
                      variant="outlined"
                      sx={{ minWidth: 100 }}
                    />
                  )}
                  <Typography ml={1} variant="h2">
                    {row.name}
                  </Typography>
                </Stack>
              </Box>
              <Box
                sx={{
                  ml: { xs: 0, sm: 'auto' },
                  mr: { xs: 0, sm: 2 },
                  mt: { xs: 1, sm: 0 },
                  width: { xs: '100%', sm: 'auto' },
                }}
              >
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} width="100%">
                  {isCateringManager && (
                    <Button
                      fullWidth={false}
                      sx={{ width: { xs: '100%', sm: 'auto' } }}
                      variant="outlined"
                      onClick={(event) => {
                        // A collection is org-shared only if it has a buyerOrgId
                        const isOrgShared = row.buyerOrgId !== null;
                        handleControlAccess(event, row.id, row.buyerSites, isOrgShared);
                      }}
                    >
                      Control Access
                    </Button>
                  )}
                  {canModify && (
                    <>
                      <Button
                        fullWidth={false}
                        sx={{ width: { xs: '100%', sm: 'auto' } }}
                        variant="outlined"
                        onClick={(event) => {
                          event.stopPropagation();
                          handleEditClick(event, row.id, row.name);
                        }}
                      >
                        Rename
                      </Button>
                      <Button
                        fullWidth={false}
                        sx={{ width: { xs: '100%', sm: 'auto' } }}
                        variant="outlined"
                        onClick={(event) => {
                          event.stopPropagation();
                          handleDeleteClick(row.id, row.name);
                        }}
                      >
                        Delete list
                      </Button>
                    </>
                  )}
                </Stack>
              </Box>
            </Stack>
          </AccordionSummary>
          <AccordionDetails sx={{ p: { xs: 0, md: 2 } }}>
            {cart && <FavouriteProductsList cart={cart} items={row.items} collectionId={row.id} />}
          </AccordionDetails>
        </Accordion>
      );
    });

  return (
    <ACHelmetContainer maxWidth={settings.themeStretch ? false : 'xl'} title="Favourites">
      <GoBackHeader heading="Favourites Lists" disableGoBack />

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', sm: 'center' }}
        gap={2}
        mb={2}
        ml={{ xs: 1, sm: 0 }}
        mr={{ xs: 1, sm: 0 }}
      >
        <Tabs value={currentTab} onChange={handleTabChange}>
          <Tab label="Personal" value="personal" />
          <Tab label="Shared" value="shared" />
        </Tabs>
        <Stack direction="row" gap={2} alignItems="center" justifyContent="space-between" flex={1}>
          {currentTab === 'shared' && isCateringManager ? (
            <FormControl sx={{ minWidth: { xs: 150, sm: 200 } }}>
              <InputLabel>Filter by Site</InputLabel>
              <Select
                value={selectedBuyerSiteId}
                onChange={handleBuyerSiteFilterChange}
                label="Filter by Site"
              >
                <MenuItem value="">All Sites</MenuItem>
                {buyerSites.map((site: any) => (
                  <MenuItem key={site.id} value={site.id}>
                    {site.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <Box />
          )}
          {(currentTab === 'personal' || isCateringManager) && (
            <Button variant="contained" onClick={() => setAddListOpen(true)}>
              Add New List
            </Button>
          )}
        </Stack>
      </Stack>
      <Stack direction="column" flex={1} gap={2}>
        <LoadingBarHorizontal showLoading={!!isFavouritesCollectionsLoading} />
        {filteredCollections.length > 0 && cart ? (
          renderAccordions(filteredCollections)
        ) : !isFavouritesCollectionsLoading && !favouritesColllectionsError ? (
          <Typography>No Favourites Lists found</Typography>
        ) : null}
      </Stack>

      {collectionToEdit && (
        <EditFavouritesNameDialog
          open={editDialogOpen}
          onClose={() => {
            setEditDialogOpen(false);
            setCollectionToEdit(null);
          }}
          onSubmit={handleEditSubmit}
          initialName={collectionToEdit?.name || ''}
          title="Rename Favourites List"
        />
      )}

      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        collectionName={collectionToDelete?.name}
      />
      <AddFavouritesListDialog
        open={addListOpen}
        onClose={() => setAddListOpen(false)}
        currentTab={currentTab}
      />
      {selectedCollection && (
        <ControlAccessDialog
          open={controlAccessOpen}
          onClose={handleCloseControlAccess}
          collectionId={selectedCollection.id}
          currentBuyerSites={selectedCollection.buyerSites}
          buyerSites={buyerSites}
          isSharedWithOrg={selectedCollection.isSharedWithOrg}
        />
      )}
    </ACHelmetContainer>
  );
}
